/**
 * Deterministic day-seeded rotation utilities.
 *
 * Homepage surfaces rotate once per LOCAL day: a visitor sees the same
 * selection all day and it changes at their midnight. Everything here is pure
 * and derives from the date, so tests can pin any day. Never parse a
 * 'YYYY-MM-DD' string with `new Date(str)` (UTC trap) — build dates from
 * components, and compare date keys lexicographically (safe when zero-padded).
 */

/** 'YYYY-MM-DD' in the visitor's local timezone. */
export function localDateKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** FNV-1a over the local date key + salt: a stable uint32 seed for the day. */
export function dateSeed(date: Date = new Date(), salt = 0): number {
  const input = `${localDateKey(date)}#${salt}`;
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** The day's pick from a pool. Distinct salts decorrelate surfaces. */
export function pickDaily<T>(items: readonly T[], date?: Date, salt = 0): T | undefined {
  if (items.length === 0) return undefined;
  return items[dateSeed(date, salt) % items.length];
}

/** mulberry32 — tiny deterministic PRNG, plenty for shuffling content. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** `count` distinct items for the day (seeded Fisher-Yates, clamped to pool size). */
export function pickDailyMany<T>(items: readonly T[], count: number, date?: Date, salt = 0): T[] {
  const pool = [...items];
  const rand = mulberry32(dateSeed(date, salt));
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.max(0, Math.min(count, pool.length)));
}

/** Per-surface salts so shared pools don't rotate in lockstep. */
export const ROTATION_SALTS = {
  hero: 1,
  reads: 2,
  coverline: 3,
} as const;
